// src/pages/admin/EditRolePage.jsx
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';

// Import components
import RoleForm from '../../components/roles/RoleForm';
import PageHeader from '../../components/ui/PageHeader';

// Import stores
import useRoleStore from '../../stores/roleStore';
import useNotificationStore from '../../stores/notificationStore';
import { MagicLoading } from '../../components/ui';

/**
 * Page for editing an existing role
 */
const EditRolePage = () => {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const { 
    role, 
    loading, 
    error, 
    fetchRole, 
    updateRole, 
    clearRole, 
    clearError 
  } = useRoleStore();
  const { showSuccess, showError } = useNotificationStore();
  
  // Load role data when component mounts
  useEffect(() => {
    if (roleId) {
      fetchRole(roleId);
    }
    
    // Clean up when component unmounts
    return () => {
      clearRole();
      clearError();
    };
  }, [roleId, fetchRole, clearRole, clearError]);
  
  // Handle form submission
  const handleSubmit = async (roleData) => {
    try {
      await updateRole(roleId, roleData);
      showSuccess('Role updated successfully');
      navigate('/admin/roles');
    } catch (err) {
      showError(err.message || 'Failed to update role');
      throw err;
    }
  };
  
  // Handle form cancellation
  const handleCancel = () => {
    navigate('/admin/roles');
  };
  
  // Show loading state while fetching role
  if (loading && !role) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Show error if failed to load role
  if (error && !role) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          sx={{ maxWidth: 600, mx: 'auto' }}
          action={
            <Box>
              <Alert.Button onClick={() => fetchRole(roleId)}>
                Retry
              </Alert.Button>
              <Alert.Button onClick={() => navigate('/admin/roles')}>
                Back to Roles
              </Alert.Button>
            </Box>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={{ position: 'relative' }}>
      {loading && <MagicLoading />}
      
      <PageHeader
        title="Edit Role"
        subtitle={role ? `Editing "${role.name}" role` : 'Update role details and permissions'}
        icon="security"
        backTo="/admin/roles"
      />
      
      <Box sx={{ mt: 3 }}>
        {role && (
          <RoleForm
            role={role}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        )}
      </Box>
    </Box>
  );
};

export default EditRolePage;